package com.evoapps.evoshift.data.remote

import io.ktor.client.HttpClient
import io.ktor.client.plugins.onUpload
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsBytes
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.http.encodeURLParameter
import io.ktor.http.isSuccess
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

/** A Firebase Storage request failed with HTTP [status] (403: storage.rules refused it). */
class FirebaseStorageException(val status: Int, message: String) : Exception(message)

/**
 * Firebase Storage over its REST API (`/v0/b/{bucket}/o`, what the Firebase SDKs use) with the
 * signed-in user's ID token, so every platform shares this code without a platform Storage SDK,
 * like [CloudFunctionsClient]. storage.rules apply to every call: uploads, reads and deletes.
 */
class FirebaseStorageClient(
    private val httpClient: HttpClient,
    private val baseUrl: () -> String,
    private val bucket: () -> String,
    /** SH-185: the App Check token, sent along where the platform installed App Check. */
    private val appCheckToken: suspend () -> String? = { AppCheckTokens.current() },
    private val idToken: suspend () -> String?
) {
    private val json = Json { ignoreUnknownKeys = true }

    private fun objectUrl(path: String) = "${baseUrl()}/v0/b/${bucket()}/o/${path.encodeURLParameter()}"

    /**
     * The address of [path]'s content. [version] (the object's generation) makes each upload a
     * new address, so no cache shows the replaced file. Reading it still needs the sign-in.
     */
    fun downloadUrl(path: String, version: String?): String =
        objectUrl(path) + "?alt=media" + (version?.let { "&v=${it.encodeURLParameter()}" } ?: "")

    /** Whether [url] is an object of this project's bucket (read with the sign-in). */
    fun isOwnUrl(url: String): Boolean = url.startsWith("${baseUrl()}/v0/b/${bucket()}/o/")

    /**
     * Uploads [bytes] to [path] (a simple media upload; [contentType] is what storage.rules see)
     * and returns the new object's generation. [onProgress] gets the sent fraction, 0..1.
     */
    suspend fun upload(path: String, bytes: ByteArray, contentType: String, onProgress: (Float) -> Unit = {}): String? {
        val token = requireToken()
        val appCheck = appCheckToken()
        val response = httpClient.post("${baseUrl()}/v0/b/${bucket()}/o") {
            parameter("uploadType", "media")
            parameter("name", path)
            authorize(token, appCheck)
            contentType(ContentType.parse(contentType))
            setBody(bytes)
            onUpload { sent, total ->
                val size = total ?: bytes.size.toLong()
                if (size > 0) onProgress((sent.toFloat() / size).coerceIn(0f, 1f))
            }
        }
        val body = response.checked("upload").bodyAsText()
        onProgress(1f)
        return runCatching { json.parseToJsonElement(body).jsonObject["generation"]?.jsonPrimitive?.contentOrNull }.getOrNull()
    }

    /** The content of [url], an address made by [downloadUrl]. */
    suspend fun download(url: String): ByteArray {
        require(isOwnUrl(url)) { "Not an object of this bucket" }
        val token = requireToken()
        val appCheck = appCheckToken()
        return httpClient.get(url) { authorize(token, appCheck) }.checked("download").bodyAsBytes()
    }

    /** Deletes [path]; true if it existed, false if there was nothing to delete. */
    suspend fun delete(path: String): Boolean {
        val token = requireToken()
        val appCheck = appCheckToken()
        val response = httpClient.delete(objectUrl(path)) { authorize(token, appCheck) }
        if (response.status == HttpStatusCode.NotFound) return false
        response.checked("delete")
        return true
    }

    private fun HttpRequestBuilder.authorize(token: String, appCheck: String?) {
        header(HttpHeaders.Authorization, "Firebase $token")
        if (appCheck != null) header(AppCheckTokens.HEADER, appCheck)
    }

    private suspend fun requireToken(): String =
        idToken() ?: throw FirebaseStorageException(HttpStatusCode.Unauthorized.value, "You must be signed in.")

    private fun HttpResponse.checked(what: String): HttpResponse {
        if (!status.isSuccess()) throw FirebaseStorageException(status.value, "Storage $what failed with HTTP ${status.value}")
        return this
    }

    companion object {
        const val PRODUCTION_BASE_URL = "https://firebasestorage.googleapis.com"
    }
}
